<script lang="ts" setup>
    import '../../../../css/choice.css';
    import {onMounted,watch,reactive,computed} from 'vue';
    import {popAlert,ModalStyles} from '../../../../src/lib/Notification';
    import {RefundArea,refundRecordTypes,IUpdatedClaimEntityRefunds,ClaimColumnDefinitions,ClaimDisplay} from '../../../../src/lib/Models/ClaimModel';
    import {ErrorReturnMessage,getClaimEntityRefunds} from '../../../../src/lib/Api/Claim'; 
    import FloatInput from "../../../components/FloatInput.vue";
    import {initPinia} from '../../../../src/lib/PiniaMgr';
    import {useRefundFullfillmentStore} from '../../../../src/lib/Stores/RefundFullfillmentStore';
    
    const refundFullfillmentStore = useRefundFullfillmentStore(initPinia());
    const formData = reactive <
    {   
        autoDistribute: boolean,
        claimID? : number,
        claimBalance?: number,
        responsiblePartyEID? :number,
        primaryPayerEID? :number,
        secondaryPayerEID? :number,
        tertiaryPayerEID? :number, 
        updatedRefundData? : IUpdatedClaimEntityRefunds[],
        refundsFound: boolean,
        cssClassBorderDanger: string
    }>({   
        autoDistribute: false,
        refundsFound: false,
        cssClassBorderDanger: "rounded border border-danger"
    });
    const props = withDefaults(defineProps<{ 
        saveEnabled?: number,
        autoDistribute: boolean,
        claimID? : number,
        display: ClaimDisplay,
        claimBalance? : number,
        responsiblePartyEID? : number,
        responsiblePartyName : string,
        primaryPayerEID? : number,
        secondaryPayerEID? : number,
        tertiaryPayerEID? : number
    }>(),
    {
        saveEnabled: 0,
        autoDistribute: false,
        responsiblePartyName: "**Responsible Party**"
    }); 
    onMounted(async () => {
        formData.autoDistribute = props.autoDistribute
        formData.claimID = props.claimID;
        formData.claimBalance = props.claimBalance;
        formData.responsiblePartyEID = props.responsiblePartyEID;
        formData.primaryPayerEID = props.primaryPayerEID;
        formData.secondaryPayerEID = props.secondaryPayerEID;
        formData.tertiaryPayerEID = props.tertiaryPayerEID;
    });
    watch(() => 
        [props.claimID,props.claimBalance,props.responsiblePartyEID,props.primaryPayerEID,props.secondaryPayerEID,props.tertiaryPayerEID], 
        ([newID,newBalance,newResponsiblePartyEID,newPrimaryPayerEID,newSecondaryPayerEID,newTertiaryPayerEID], 
         [oldID,oldBalance,oldResponsiblePartyEID,oldPrimaryPayerEID,oldSecondaryPayerEID,oldTertiaryPayerEID]) => {  
            formData.claimID = newID;
            formData.claimBalance = newBalance;
            formData.responsiblePartyEID = newResponsiblePartyEID;
            formData.primaryPayerEID = newPrimaryPayerEID;
            formData.secondaryPayerEID = newSecondaryPayerEID;
            formData.tertiaryPayerEID = newTertiaryPayerEID;
            SetRefundIcons();
        }
    );
    watch(() => 
            props.autoDistribute,(newValue,oldValue)=> {
            if (newValue != oldValue) {
                formData.autoDistribute = newValue;
                SetRefundIcons();
            }
        }
    );

    async function SetRefundIcons() : Promise<void> {       
        if (!formData.claimID) {
            return;
        }
        const claimRefundGet = await getClaimEntityRefunds(formData.claimID); 
        if (!claimRefundGet.success) {  
            await popAlert(claimRefundGet.error?.error ?? ErrorReturnMessage.refunds,"Refund Details",ModalStyles.danger);
            return;
        }  
        const refund_data = claimRefundGet.success;
        if (props.responsiblePartyEID) {
            const initialValue = 0;
            const refund_total = claimRefundGet.success.map(item => item.refundTotal).flat().reduce((accumulator, currentValue) => accumulator + currentValue, initialValue);
            const missingCredit = (refund_total + (formData.claimBalance ?? 0)) * -1;  
            if (missingCredit > 0) {
                refund_data.push({refundTotal: missingCredit, refundEID: props.responsiblePartyEID, refundArea: RefundArea.Missing, payerType: "responsible_party", payerName: props.responsiblePartyName});
            } 
        }
        formData.updatedRefundData = 
            refund_data.map(item => (
                {
                    claimID: props.claimID ?? 0, 
                    refundTotal: ((props.claimBalance ?? 0) >= 0 || (props.display === ClaimDisplay.RefundTransfer && item.payerType === refundFullfillmentStore.refundPayerType.payer) ?  0 : item.refundTotal),
                    newRefundTotal: (!formData.autoDistribute || (props.claimBalance ?? 0) >= 0 || (props.display === ClaimDisplay.RefundTransfer && item.payerType === refundFullfillmentStore.refundPayerType.payer) ?  0 : item.refundTotal), 
                    refundEID: item.refundEID, 
                    refundArea: item.refundArea, 
                    payerType: item.payerType, 
                    payerName: item.payerName
                }));                   
        formData.refundsFound = (formData.updatedRefundData.length ? true : false);  
        refundFullfillmentStore.updateRefunds(formData.updatedRefundData);
    }
    function refundUpdate(new_amount : number|undefined) : void {  
        if (formData.updatedRefundData) { 
            refundFullfillmentStore.updateRefunds(formData.updatedRefundData);
        }
    }
</script> 
 
<template> 
    <div v-if="!formData.refundsFound" class="label-isalus-small text-secondary" title="This claim has no refunds and no responsible party, no actions can be performed.">
        No refunds.
    </div>
    <table v-if="formData.refundsFound" :style="`width: ${ClaimColumnDefinitions.Input - 1}rem`">  
        <colgroup>
            <col style="width: 1rem">
            <col :style="`width: ${ClaimColumnDefinitions.Input - 2}rem`">
        </colgroup>
        <tr v-for="(refundRecord,index) in formData.updatedRefundData" :key="`Ref${refundRecord.refundArea}${refundRecord.refundEID}`" >
            <td>
                <i 
                    :class="`${refundRecordTypes[refundRecord.refundArea].iconClassStyle} label-isalus-small `"
                    :title="`${refundRecordTypes[refundRecord.refundArea].iconTooltip(refundRecord.payerName,refundRecord.refundTotal,refundRecord.refundEID)}`"
                />
            </td>
            <td> 
                <span v-if="((props.claimBalance ?? 0) >= 0) || (props.display === ClaimDisplay.RefundTransfer && refundRecord.payerType === refundFullfillmentStore.refundPayerType.payer)" 
                    :title="((props.claimBalance ?? 0) > 0 ? 'No action can be performed: claim balance is greater than/equal to zero.' : 'No action can be peformed: claim transfers cannot occur for payers.')">
                    N/A
                </span>
                <div :class="refundFullfillmentStore.transferTotals.AllocateErrorLessThan ? formData.cssClassBorderDanger : ''">
                <FloatInput v-if="((props.claimBalance ?? 0) < 0) && (!(props.display === ClaimDisplay.RefundTransfer && refundRecord.payerType === refundFullfillmentStore.refundPayerType.payer))"
                    v-model:numberValue="refundRecord.newRefundTotal"
                    :maxLength="10"
                    :startingRange="0"
                    :endingRange="refundRecord.refundTotal"
                    :minPrecision="2"
                    :maxPrecision="2"
                    :positive="true"
                    :errorDisplay="false"
                    :cssFramework="'vanilla'"
                    :placeholder="'refund'"
                    @update:numberValue="refundUpdate"
                />
                </div>
            </td>
        </tr>
    </table> 
</template>
